/**
 * P6-1 群发话术活动（manager / director / admin）
 *
 * MVP：列表 + 新建（3 步：筛选 → 编写 → 预览发送）
 * 销售员看自己的待发清单走 /broadcast/my-pending（嵌入 dashboard 后续做）
 */
import { useState } from 'react';
import { Modal, Input, Radio, message, Steps } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, TableWrap } from '../../components/ui';

export function BroadcastCampaignsPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const { data: list } = useQuery<any[]>({
    queryKey: ['broadcast-campaigns'],
    queryFn: () => $.get('/broadcast/campaigns'),
  });

  return (
    <>
      <PageHeader
        breadcrumb={['主管', '群发话术']}
        title="📨 群发话术活动"
        subtitle="按筛选条件锁定客户名单 → 编写话术 → 销售员复制发送给客户"
        actions={<Btn kind="primary" onClick={() => setShowNew(true)}>+ 新建群发</Btn>}
      />
      <div className="work-content">
        <HomeCard title={`历史活动（${list?.length ?? 0}）`}>
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>标题</th>
                  <th>客户数</th>
                  <th>已发</th>
                  <th>完成率</th>
                  <th>状态</th>
                  <th>创建时间</th>
                </tr>
              </thead>
              <tbody>
                {(list ?? []).length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无活动</td></tr>
                )}
                {(list ?? []).map((c: any) => (
                  <tr key={c.id}>
                    <td>{c.title}</td>
                    <td>{c.targetCustomerCount}</td>
                    <td>{c.sentCount}</td>
                    <td>
                      <Pill tone={c.targetCustomerCount > 0 && c.sentCount === c.targetCustomerCount ? 'success' : 'warning'}>
                        {c.targetCustomerCount > 0 ? Math.round(c.sentCount / c.targetCustomerCount * 100) : 0}%
                      </Pill>
                    </td>
                    <td><Pill tone="default">{c.status}</Pill></td>
                    <td>{new Date(c.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>

      <NewBroadcastModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onDone={() => {
          setShowNew(false);
          qc.invalidateQueries({ queryKey: ['broadcast-campaigns'] });
        }}
      />
    </>
  );
}

function NewBroadcastModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<any>({
    title: '',
    script_content: '',
    target_filter: { stages: [], demand_type: null },
    send_method: 'manual_copy',
  });

  const submit = useMutation({
    mutationFn: () => $.post('/broadcast/campaigns', form),
    onSuccess: (r: any) => {
      message.success(`已创建：${r.recipients} 位客户，${r.salesNotified} 位销售员收到任务`);
      setStep(0);
      onDone();
    },
    onError: (e: any) => message.error(e?.message ?? '创建失败'),
  });

  return (
    <Modal title="新建群发话术活动" open={open} onCancel={onClose} width={680}
           footer={
             <>
               {step > 0 && <Btn kind="default" onClick={() => setStep(step - 1)}>上一步</Btn>}
               {step < 2 && <Btn kind="primary" onClick={() => setStep(step + 1)}>下一步</Btn>}
               {step === 2 && (
                 <Btn kind="primary" onClick={() => submit.mutate()} disabled={submit.isPending}>
                   {submit.isPending ? '创建中…' : '创建并通知销售员'}
                 </Btn>
               )}
             </>
           }>
      <Steps current={step} size="small" style={{ marginBottom: 24 }}
             items={[{ title: '筛选客户' }, { title: '编写话术' }, { title: '预览发送' }]} />
      {step === 0 && (
        <>
          <Field label="客户阶段">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['L1', 'L2', 'L3', 'L4', 'L5', 'L6'].map((s) => {
                const sel = (form.target_filter.stages as string[]).includes(s);
                return (
                  <button key={s}
                          className={`filter-chip ${sel ? 'active' : ''}`}
                          onClick={() => {
                            const cur = (form.target_filter.stages as string[]) ?? [];
                            const next = sel ? cur.filter((x) => x !== s) : [...cur, s];
                            setForm({ ...form, target_filter: { ...form.target_filter, stages: next } });
                          }}
                          style={sel ? { background: 'var(--primary-bg)', borderColor: 'var(--primary)', color: 'var(--primary)' } : {}}>
                    {s}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="需求类型">
            <Radio.Group value={form.target_filter.demand_type ?? ''}
                         onChange={(e) => setForm({ ...form, target_filter: { ...form.target_filter, demand_type: e.target.value || null } })}>
              <Radio value="">全部</Radio>
              <Radio value="work_cert">🎯 工作考证</Radio>
              <Radio value="fulltime_shop">🏪 全职做店铺</Radio>
              <Radio value="sideline_shop">💰 副业做店铺</Radio>
              <Radio value="brand_overseas">🌍 品牌出海</Radio>
            </Radio.Group>
          </Field>
        </>
      )}
      {step === 1 && (
        <>
          <Field label="活动标题">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="如：5 月底优惠通知" />
          </Field>
          <Field label="话术内容">
            <Input.TextArea value={form.script_content} rows={8}
                            onChange={(e) => setForm({ ...form, script_content: e.target.value })}
                            placeholder="复制后通过微信发给客户的话术..." />
          </Field>
        </>
      )}
      {step === 2 && (
        <>
          <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-2)' }}>
            📊 预览
          </div>
          <div style={{ padding: 12, background: 'var(--bg-2)', borderRadius: 8, marginBottom: 12 }}>
            <div><strong>标题：</strong>{form.title || '（未填）'}</div>
            <div><strong>筛选：</strong>阶段 {(form.target_filter.stages ?? []).join(', ') || '全部'} ·
              需求 {form.target_filter.demand_type ?? '全部'}</div>
          </div>
          <div style={{ padding: 12, background: 'var(--primary-bg)', borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>话术：</div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{form.script_content || '（未填）'}</div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--warning-text)', marginTop: 12 }}>
            ⚠️ 点"创建"后系统给所有匹配客户的销售员推钉钉通知，让他们去客户列表"待发送话术"复制发送。
          </div>
        </>
      )}
    </Modal>
  );
}

function Field({ label, children }: any) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, marginBottom: 6, color: 'var(--text-2)' }}>{label}</div>
      {children}
    </div>
  );
}
